import {generateGuid} from "../../../shared/services/utils";
import yjsDocumentService from "../../../infrastructure/sync/YjsDocumentService";
import {isWorkspaceReadOnly} from '../../../shared/services/workspaceAccess';
import {getLocalBackups, storeLocalBackup, deleteLocalBackup} from './local-backups';
import {getWorkspaceId} from "../../../shared/services/dbUtils";

const YJS_BACKUP_LIST_KEY = 'backups';
const observers = new Set();

/**
 * Gets the YJS document for the given workspace ID
 */
const getYjsDocument = () => {
  try {
    const { doc } = yjsDocumentService.connectToDocument(getWorkspaceId());
    return doc;
  } catch (error) {
    console.error('Error connecting to YJS document:', error);
    throw error;
  }
};

/**
 * Notifies all registered observers about changes
 */
const notifyObservers = () => {
  observers.forEach(callback => {
    try {
      callback();
    } catch (error) {
      console.error('Error in backup observer callback:', error);
    }
  });
};

/**
 * Sets up a listener for changes to the backups map
 * Returns a function to remove the observer
 */
export const observeBackupsChanges = (callback) => {
  if (typeof callback !== 'function') {
    console.error('Observer callback must be a function');
    return () => {};
  }
  
  // Add to observers set
  observers.add(callback);
  if (isWorkspaceReadOnly(getWorkspaceId())) return () => observers.delete(callback);
  
  // Also set up YJS-specific observer
  try {
    const doc = getYjsDocument();
    const backupsMap = doc.getMap(YJS_BACKUP_LIST_KEY);
    
    // YJS observer
    const handleBackupsChange = () => {
      console.log('Backups map changed, notifying observers...');
      notifyObservers();
    };
    
    // Subscribe to YJS changes
    backupsMap.observe(handleBackupsChange);
    
    // Return function to remove the observer
    return () => {
      observers.delete(callback);
      try {
        backupsMap.unobserve(handleBackupsChange);
      } catch (error) {
        console.error('Error removing YJS observer:', error);
      }
    };
  } catch (error) {
    console.error('Error setting up YJS observer:', error);
    return () => {
      observers.delete(callback);
    };
  }
};

/**
 * Stores a backup entry in the YJS document
 */
export const storeBackup = async (backup) => {
  try {
    if (!backup.id) {
      backup.id = generateGuid();
    }
    if (isWorkspaceReadOnly(getWorkspaceId())) {
      await storeLocalBackup(getWorkspaceId(), backup);
      notifyObservers();
      return backup;
    }

    const doc = getYjsDocument(getWorkspaceId());
    const backupsMap = doc.getMap(YJS_BACKUP_LIST_KEY);

    doc.transact(() => {
      backupsMap.set(backup.id, backup);
    });
    
    // Notify observers manually (in addition to YJS observer)
    // This ensures all code is notified even if YJS observer is delayed
    notifyObservers();

    return backup;
  } catch (error) {
    console.error('Error storing backup:', error);
    throw error;
  }
};

/**
 * Retrieves all backups sorted by date (newest first)
 */
export const getBackups = async () => {
  try {
    if (isWorkspaceReadOnly(getWorkspaceId())) return await getLocalBackups(getWorkspaceId());
    const doc = getYjsDocument(getWorkspaceId());
    const backupsMap = doc.getMap(YJS_BACKUP_LIST_KEY);

    const backups = [];
    backupsMap.forEach((value) => {
      backups.push(value);
    });

    return backups.sort((a, b) => new Date(b.date) - new Date(a.date));
  } catch (error) {
    console.error('Error getting backups:', error);
    return [];
  }
};

/**
 * Retrieves a specific backup by ID
 */
export const getBackup = async (id) => {
  try {
    if (isWorkspaceReadOnly(getWorkspaceId())) return (await getLocalBackups(getWorkspaceId())).find(backup => backup.id === id) || null;
    const doc = getYjsDocument(getWorkspaceId());
    const backupsMap = doc.getMap(YJS_BACKUP_LIST_KEY);

    return backupsMap.get(id) || null;
  } catch (error) {
    console.error(`Error getting backup with ID ${id}:`, error);
    return null;
  }
};

/**
 * Deletes a backup by ID
 */
export const deleteBackup = async (id) => {
  try {
    if (isWorkspaceReadOnly(getWorkspaceId())) {
      await deleteLocalBackup(getWorkspaceId(), id);
      notifyObservers();
      return true;
    }
    const doc = getYjsDocument(getWorkspaceId());
    const backupsMap = doc.getMap(YJS_BACKUP_LIST_KEY);

    if (backupsMap.has(id)) {
      doc.transact(() => {
        backupsMap.delete(id);
      });
      
      // Notify observers manually (in addition to YJS observer)
      notifyObservers();
      
      return true;
    }

    return false;
  } catch (error) {
    console.error(`Error deleting backup with ID ${id}:`, error);
    return false;
  }
};

/**
 * Removes old backups keeping only the specified number of most recent ones
 */
export const deleteOldBackups = async ( keepCount = 10) => {
  try {
    const backups = await getBackups(getWorkspaceId());

    // Skip if we don't have more than the desired number of backups
    if (backups.length <= keepCount) return true;

    // Get backups to delete (older ones beyond the keepCount)
    const backupsToDelete = backups.slice(keepCount);
    if (isWorkspaceReadOnly(getWorkspaceId())) {
      for (const backup of backupsToDelete) await deleteLocalBackup(getWorkspaceId(), backup.id);
      notifyObservers();
      return true;
    }

    const doc = getYjsDocument(getWorkspaceId());
    const backupsMap = doc.getMap(YJS_BACKUP_LIST_KEY);

    doc.transact(() => {
      for (const backup of backupsToDelete) {
        backupsMap.delete(backup.id);
      }
    });
    
    // Notify observers manually after deleting old backups
    notifyObservers();

    return true;
  } catch (error) {
    console.error('Error deleting old backups:', error);
    return false;
  }
};
